/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import {
  Grid,
  Paper,
  Table,
  TableContainer,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import JsBarcode from 'jsbarcode';

import { BasePrintComponentProps, OrderType } from 'commons/types';
import { formatCurrency } from 'commons/utils/formatCurrency';

import { IInvoiceMappedData, IRenderedPickingItem } from '../../interfaces';

import S from './InvoicePrintTemplate.styles';

interface InvoicePrintTemplateComponentProps
  extends BasePrintComponentProps<IInvoiceMappedData> {
  setMaxPageNumber?: (page: number) => void;
  copyNumber?: number; // to adjust footer position if this component is copy version of template printed
}

const BarcodeImage = ({ value }: { value: string }) => {
  const barcodeRef = React.useRef(null);

  useEffect(() => {
    JsBarcode(barcodeRef.current, value, {
      format: 'CODE128',
      width: 1.5,
      height: 50,
      displayValue: true,
      fontSize: 10,
    });
  }, [value]);

  return <img ref={barcodeRef} alt="Barcode" />;
};

const getMaxPageNumber = (tableData: IRenderedPickingItem[][]): number => {
  const additionalPage = tableData[tableData.length - 1].length > 10 ? 1 : 0;

  return tableData.length + additionalPage;
};

export const InvoicePrintTemplateComponent = ({
  data,
  setMaxPageNumber,
  copyNumber = 0,
}: InvoicePrintTemplateComponentProps): React.JSX.Element => {
  const [maxPage, setMaxPage] = useState<number>(0);
  const [invoiceTitle, setInvoiceTitle] = useState<string>('');

  useEffect(() => {
    switch (data.po_type) {
      case OrderType.PRECURSOR:
        setInvoiceTitle('Prekursor');
        break;
      case OrderType.ALKES:
        setInvoiceTitle('Alkes');
        break;
      case OrderType.CCP:
        setInvoiceTitle('Produk Rantai Dingin');
        break;
      case OrderType.PSIKOTROPIKA:
        setInvoiceTitle('Psikotropika');
        break;
      case OrderType.OOT:
        setInvoiceTitle('Obat-Obat Tertentu');
        break;
      default:
        setInvoiceTitle('');
        break;
    }
  }, [data, setInvoiceTitle]);
  const renderHeader = () => (
    <S.PageHeader>
      <S.HeaderCopeWrapper container>
        <Grid item xs={6} style={{ textAlign: 'center' }}>
          <S.DigitalSignatureLabel>
            {data.is_digital_signed ? 'SP Electronic' : 'SP Manual'}
          </S.DigitalSignatureLabel>
        </Grid>
        <Grid item xs={6} style={{ textAlign: 'center' }}>
          <S.FakturLabel>FAKTUR</S.FakturLabel>
        </Grid>
      </S.HeaderCopeWrapper>
      {/* Invoice Cope Section */}
      <S.InvoiceCopeWrapper container>
        <Grid item xs={1}>
          <img
            src="/assets/print-template/TMPLogo.png"
            alt="logo"
            style={{
              width: '250%',
              marginTop: '24px',
              marginLeft: '-40px',
            }}
          />
        </Grid>
        {/* Middle Section Company Profile */}
        <S.CompanyProfileWrapper item xs={6}>
          <div>
            <b>PT. Teknologi Medika Pratama</b>
          </div>
          {data.company_info?.name?.includes('Surabaya') && (
            <S.CompanyAddressText>
              {data.company_info?.name}
            </S.CompanyAddressText>
          )}
          <S.CompanyAddressText>
            {data.company_info?.address}
          </S.CompanyAddressText>
          <div>No Izin PBF: {data.company_info?.pbf_no}</div>
          <div>No Izin DAK: {data.company_info?.dak_no}</div>
          <div>No Sertifikat CDOB : {data.company_info?.cdob_no}</div>
          <div>No Sertifikat CDAKB : {data.company_info?.cdakb_no}</div>
          {data.company_info?.cdob_ccp_no && (
            <div>No Sertifikat CDOB CCP : {data.company_info?.cdob_ccp_no}</div>
          )}
        </S.CompanyProfileWrapper>
        {/* Right Section Nomor Faktur etc */}
        <Grid item xs={5} style={{ paddingTop: 4 }}>
          <Grid container>
            <Grid item xs={4}>
              <b>Nomor Faktur</b>
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={7}>
              {data.invoice_number}
            </Grid>
            {invoiceTitle && (
              <Grid item xs={12}>
                <S.OrderTypeText>{invoiceTitle}</S.OrderTypeText>
              </Grid>
            )}
            <Grid item xs={4}>
              Tanggal
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={7}>
              {data.invoice_date}
            </Grid>
            <Grid item xs={4}>
              Nomor PO
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={7}>
              {data.po_number}
            </Grid>
            <Grid item xs={4}>
              Jatuh Tempo
            </Grid>
            <Grid item xs={1}>
              :
            </Grid>
            <Grid item xs={7}>
              {data.due_date}
            </Grid>
          </Grid>
        </Grid>
      </S.InvoiceCopeWrapper>

      {/* Customer Info Section */}
      <S.CustomerInfoWrapper container>
        <S.CustomerInfoContent item xs={1}>
          <b>Customer</b>
          <span style={{ marginLeft: 4 }}>:</span>
        </S.CustomerInfoContent>
        <S.CustomerInfoContent item xs={11}>
          {data.customer_name}
        </S.CustomerInfoContent>
        <S.CustomerInfoContent item xs={1}>
          <b>Alamat</b>
          <span style={{ marginLeft: 20 }}>:</span>
        </S.CustomerInfoContent>
        <S.CustomerInfoContent item xs={11}>
          {data.customer_address}
        </S.CustomerInfoContent>
      </S.CustomerInfoWrapper>
    </S.PageHeader>
  );

  // determine max page data
  useEffect(() => {
    const maxPageNumber = getMaxPageNumber(data.items);
    setMaxPage(maxPageNumber);

    if (setMaxPageNumber) setMaxPageNumber(maxPageNumber);
  }, [maxPage, data.items]);

  // Footer Section
  const renderFooter = () =>
    Array.from(Array(maxPage).keys()).map((idx) => (
      <S.PageFooter
        className="last-page"
        pageOffset={idx + maxPage * copyNumber}
      >
        <Grid container>
          <Grid item xs={3}>
            <QRCode size={88} value={`PT-INV-${data.po_number}`} />
          </Grid>
          <Grid item xs={7} className="print-barcode">
            <BarcodeImage value={data.invoice_number || '-'} />
          </Grid>
          <Grid item xs={2} style={{ textAlign: 'right' }}>
            <S.PageNumberText>
              Page {idx + 1} of {maxPage}
            </S.PageNumberText>
          </Grid>
        </Grid>
      </S.PageFooter>
    ));

  const renderBody = (
    itemData: IRenderedPickingItem[],
    isLastPage: boolean,
  ) => (
    <S.PageBody>
      <TableContainer component={Paper} style={{ boxShadow: 'none' }}>
        <Table
          sx={{
            minWidth: 650,
            [`& .${tableCellClasses.root}`]: {
              borderBottom: 'none',
              fontFamily: 'Arial',
              fontSize: 12,
            },
          }}
          size="small"
          aria-label="a dense table"
        >
          <S.TableHeadStyle>
            <TableRow>
              <S.TableHeaderCell width={75}>SKU</S.TableHeaderCell>
              <S.TableHeaderCell width={190}>NAMA BARANG</S.TableHeaderCell>
              <S.TableHeaderCell width={25}>UNIT</S.TableHeaderCell>
              <S.TableHeaderCell width={130}>KEMASAN</S.TableHeaderCell>
              <S.TableHeaderCell width={160}>NO. BATCH</S.TableHeaderCell>
              <S.TableHeaderCell width={55}>ED</S.TableHeaderCell>
              <S.TableHeaderCell width={75} align="right">
                RP/UNIT
              </S.TableHeaderCell>
              <S.TableHeaderCell width={35} align="right">
                DISK
              </S.TableHeaderCell>
              <S.TableHeaderCell width={80} align="right">
                TOTAL
              </S.TableHeaderCell>
            </TableRow>
          </S.TableHeadStyle>
          <S.TableBodyStyle>
            {itemData.map((productInfo, idx) => (
              <S.TableRowStyle
                key={`InvRow-${productInfo.sku_code}-${idx}`}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <S.TableBodyCell>{productInfo.sku_code}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.name}</S.TableBodyCell>
                <S.TableBodyCell align="center">
                  {productInfo.quantity}
                </S.TableBodyCell>
                <S.TableBodyCell>{productInfo.uom}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.inventory}</S.TableBodyCell>
                <S.TableBodyCell>{productInfo.expiry_date}</S.TableBodyCell>
                <S.TableBodyCell align="right">
                  {formatCurrency(productInfo.price, false)}
                </S.TableBodyCell>
                <S.TableBodyCell align="right">
                  {productInfo.discount_rate}
                </S.TableBodyCell>
                <S.TableBodyCell align="right">
                  {formatCurrency(productInfo.net_price, false)}
                </S.TableBodyCell>
              </S.TableRowStyle>
            ))}
          </S.TableBodyStyle>
        </Table>
      </TableContainer>
      {/* If not last page  */}
      {!isLastPage ? (
        <>
          <div className="pagebreak-after" />
          <div style={{ width: '100%' }} />
        </>
      ) : (
        <>
          {/* Additional page break if maxPage > data.items.length
           because rest of page will not enough to render summary section. Summary section will 
           be rendered separately in the next page */}
          {maxPage > data.items.length && (
            <>
              <div className="pagebreak-after" />
              <div style={{ width: '100%' }} />
            </>
          )}
          {/* Summary Section */}
          <S.SummarySectionWrapper container>
            <Grid item xs={7}>
              Pembayaran Faktur ini dilakukan ke rekening Virtual Account di
              aplikasi SwipeRx
            </Grid>
            <Grid item xs={5}>
              <Grid container>
                <Grid item xs={4}>
                  <b>Total Harga</b>
                </Grid>
                <Grid item xs={1}>
                  <b>:</b>
                </Grid>
                <Grid item xs={7} textAlign="right">
                  {formatCurrency(data.total_amount)}
                </Grid>
                <Grid item xs={4}>
                  <b>PPN</b>
                </Grid>
                <Grid item xs={1}>
                  <b>:</b>
                </Grid>
                <Grid item xs={7} textAlign="right">
                  {formatCurrency(data.ppn)}
                </Grid>
                <Grid item xs={4}>
                  <b>Total Tagihan</b>
                </Grid>
                <Grid item xs={1}>
                  <b>:</b>
                </Grid>
                <Grid item xs={7} textAlign="right">
                  {formatCurrency(data.total_amount_with_ppn)}
                </Grid>
              </Grid>
            </Grid>
            {/* Signature Section */}
            <S.SignatureSectionWrapper item xs={6}>
              <Grid container>
                <Grid item xs={4} style={{ display: 'flex' }}>
                  <p style={{ alignSelf: 'flex-end' }}>Diperiksa:</p>
                </Grid>
                <Grid item xs={4}>
                  {data.verify_document_url ? (
                    <QRCode size={80} value={data.verify_document_url || ''} />
                  ) : (
                    <img
                      id="inv-sign"
                      style={{ width: 120 }}
                      src={data.company_info?.apj_signature}
                      alt="ttd"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {data.company_info?.apj_abbreviation || 'APJ'} :{' '}
                  {data.company_info?.apj_name}
                </Grid>
                <Grid item xs={12}>
                  {data.company_info?.apj_license_label} :{' '}
                  {data.company_info?.apj_sipa}
                </Grid>
              </Grid>
            </S.SignatureSectionWrapper>
            <S.HandOverSectionWrapper item xs={6}>
              <Grid container>
                <Grid item xs={12}>
                  Faktur Elektronik ini dicetak dari sistem SwipeRx, tidak perlu
                  dikembalikan ke TMP
                </Grid>
              </Grid>
            </S.HandOverSectionWrapper>
          </S.SummarySectionWrapper>
        </>
      )}
    </S.PageBody>
  );

  return (
    <div>
      <S.PageWrapper>
        <TableContainer>
          <Table>
            {/* Header Section */}
            {renderHeader()}

            {/* Content Section */}
            {data.items.map((itemData, idx) =>
              renderBody(itemData, data.items.length === idx + 1),
            )}

            {/* Footer Section */}
            {renderFooter()}
          </Table>
        </TableContainer>
      </S.PageWrapper>
    </div>
  );
};
